import { Fragment, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import NumericInput from "../../../../components/Inputs/NumericInput";
import DateFilter from "../../../../components/tools/DateFilter";
import convertToEnglishNumbers from "../../../../function/convertToEnglishNumbers";
import { getMethod, postMethodWithFile } from "../../../../function/helper";
import Footer from "../../../../modules/public/footer/footer";
import Menu from "../../../../modules/public/menu/menu";
import DateRangePicker from "./Form/DateRangePicker";
import ResumeUploader from "./Form/ResumeUploader";
import "./NormalUserRegister.css";
import "./PersonnelRegister.css";

// === helper: build FormData with bracket notation ===
function isPlainObject(v) {
  return Object.prototype.toString.call(v) === "[object Object]";
}

function appendFD(fd, key, value) {
  if (value === undefined || value === null) return;

  // File / Blob
  if (value instanceof File || value instanceof Blob) {
    fd.append(key, value);
    return;
  }

  // Array
  if (Array.isArray(value)) {
    // اگر آرایه خالیه، یک ورودی خالی برای اطلاع‌رسانی به سرور می‌زنیم
    if (value.length === 0) {
      fd.append(`${key}[]`, "");
      return;
    }

    const simple = value.every(v => v === null || typeof v !== "object");
    if (simple) {
      // مقادیر ساده -> key[]
      value.forEach(v => fd.append(`${key}[]`, v));
    } else {
      // آبجکت/آرایه تو در تو -> key[index][sub]
      value.forEach((item, i) => appendFD(fd, `${key}[${i}]`, item));
    }
    return;
  }

  // Plain object -> key[sub]
  if (isPlainObject(value)) {
    Object.entries(value).forEach(([k, v]) => {
      appendFD(fd, `${key}[${k}]`, v);
    });
    return;
  }

  // primitive
  fd.append(key, String(value));
}

function buildFormData(payload = {}, fileKeys = []) {
  const fd = new FormData();
  Object.entries(payload).forEach(([k, v]) => {
    if (fileKeys.includes(k)) {
      // files => append as files[]
      if (!v) {
        fd.append(`${k}[]`, "");
        return;
      }
      if (Array.isArray(v)) {
        if (v.length === 0) {
          fd.append(`${k}[]`, "");
        } else {
          v.forEach(file => {
            if (file instanceof File || file instanceof Blob) {
              fd.append(`${k}[]`, file);
            }
          });
        }
      } else if (v instanceof File || v instanceof Blob) {
        fd.append(`${k}[]`, v);
      }
      return;
    }
    appendFD(fd, k, v);
  });
  return fd;
}

function PersonnelRegister() {
  const navigate = useNavigate();
  // بالای فایل (خارج از کامپوننت) یا داخل کامپوننت با useMemo
  const initialFormState = {
    name: "",
    lName: "",
    fatherName: "",
    birthCertificateNumber: "",
    birthDate: "",
    nationalCode: "",
    // helper province ids برای بارگذاری city ها (قبل از ارسال حذف می‌شوند)
    birthCertificateProvinceId: 0,
    birthCertificateCityId: 0,
    birthPlaceProvinceId: 0,
    birthPlaceCityId: 0,
    religion: "",
    nationality: "",
    isMarried: 0,
    isHealthy: 1,
    healthDescription: "",
    militaryStatus: 0,

    educationalBackground: [{}],
    careerExperiences: [{}],

    englishLevel: 0,
    computerSkillsWindows: 0,
    computerSkillsWord: 0,
    computerSkillsExcel: 0,
    computerSkillsImageEditing: 0,
    computerSkillsVideoEditing: 0,

    cooperateType: 0,
    requestedJob: "",
    haveInsuranceHistory: 0,
    insuranceHistoryDuration: "",
    howToHeardAboutUs: "",

    willingToTravelForJob: 0,
    willingToTrialPeriod: 0,
    workingRightNow: 0,
    canGuarantee: 0,
    provinceId: 0,
    cityId: 0,
    address: "",
    mobile: "",
    phone: "",
    email: "",
    confirmInformation: 0,
    files: [],
  };

  const [form, setForm] = useState(initialFormState);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [infoLoading, setInfoLoading] = useState(false);

  const [provinces, setProvinces] = useState([]);
  const [cityOption, setCityOption] = useState([{ value: 0, label: "..." }]);

  const militaryOptions = [
    { value: 1, label: "پایان خدمت" },
    { value: 2, label: "معاف" },
    { value: 3, label: "مشمول" },
    { value: 4, label: "بانو هستم" },
  ];

  const englishOptions = [
    { value: 1, label: "مبتدی" },
    { value: 2, label: "متوسط" },
    { value: 3, label: "خوب" },
    { value: 4, label: "عالی" },
  ];
  const cooperateOptions = [
    { value: 1, label: "تمام وقت" },
    { value: 2, label: "پاره‌وقت" },
    { value: 3, label: "پروژه‌ای" },
  ];

  // city options for birth certificate / birth place
  const [birthCertCityOptions, setBirthCertCityOptions] = useState([
    { value: 0, label: "..." },
  ]);
  const [birthPlaceCityOptions, setBirthPlaceCityOptions] = useState([
    { value: 0, label: "..." },
  ]);

  useEffect(() => {
    getProvince();
  }, []);

  const getProvince = async () => {
    setInfoLoading(true);
    try {
      const res = await getMethod("/address/v1/province/show/1");
      setProvinces(res.data || []);
    } catch (e) {
      //
    } finally {
      setInfoLoading(false);
    }
  };

  const getCities = async id => {
    setField("provinceId", Number(id));
    setForm(cur => ({ ...cur, cityId: 0 }));

    setInfoLoading(true);
    try {
      const city = await getMethod("/address/v1/city/show/" + id);
      const optionCity = [{ value: 0, label: "..." }];
      for (let i = 0; i < (city.data || []).length; i++) {
        optionCity.push({ value: city.data[i].id, label: city.data[i].faName });
      }
      setCityOption(optionCity);
    } catch (e) {
      //
    } finally {
      setInfoLoading(false);
    }
  };

  const handleChangeCity = async data => {
    setField("cityId", data.value);
    setErrors(cur => {
      delete cur.cityId;
      return { ...cur };
    });
  };

  /* birth certificate city helpers */
  const getCitiesForBirthCertificate = async provinceId => {
    setField("birthCertificateProvinceId", Number(provinceId));
    setField("birthCertificateCityId", 0);
    setBirthCertCityOptions([{ value: 0, label: "..." }]);

    if (!provinceId) return;
    setInfoLoading(true);
    try {
      const res = await getMethod("/address/v1/city/show/" + provinceId);
      const optionCity = [
        { value: 0, label: "..." },
        ...(res.data || []).map(c => ({ value: c.id, label: c.faName })),
      ];
      setBirthCertCityOptions(optionCity);
    } catch (e) {
      //
    } finally {
      setInfoLoading(false);
    }
  };

  const handleChangeBirthCertificateCity = opt => {
    setField("birthCertificateCityId", opt?.value ?? 0);
  };

  /* birth place city helpers */
  const getCitiesForBirthPlace = async provinceId => {
    setField("birthPlaceProvinceId", Number(provinceId));
    setField("birthPlaceCityId", 0);
    setBirthPlaceCityOptions([{ value: 0, label: "..." }]);

    if (!provinceId) return;
    setInfoLoading(true);
    try {
      const res = await getMethod("/address/v1/city/show/" + provinceId);
      const optionCity = [
        { value: 0, label: "..." },
        ...(res.data || []).map(c => ({ value: c.id, label: c.faName })),
      ];
      setBirthPlaceCityOptions(optionCity);
    } catch (e) {
      //
    } finally {
      setInfoLoading(false);
    }
  };

  const handleChangeBirthPlaceCity = opt => {
    setField("birthPlaceCityId", opt?.value ?? 0);
  };

  const setField = (field, value) => {
    setForm(cur => ({ ...cur, [field]: value }));
    if (errors[field]) setErrors(cur => ({ ...cur, [field]: null }));
  };

  const [educationalCityOptions, setEducationalCityOptions] = useState({});

  const addEducation = () => {
    setForm(cur => ({
      ...cur,
      educationalBackground: [
        ...cur.educationalBackground,
        {
          certificate: "",
          field: "",
          academicAverage: "",
          startDate: "",
          endDate: "",
          institutionName: "",
          provinceId: null,
          institutionCityId: null,
        },
      ],
    }));

    setEducationalCityOptions(prev => {
      const next = { ...prev };
      const newIndex =
        Object.keys(next).length === 0
          ? 0
          : Math.max(...Object.keys(next).map(Number)) + 1;
      next[newIndex] = [{ value: 0, label: "..." }];
      return next;
    });
  };

  const removeEducation = idx => {
    setForm(cur => ({
      ...cur,
      educationalBackground: cur.educationalBackground.filter(
        (_, i) => i !== idx
      ),
    }));

    setEducationalCityOptions(prev => {
      const newOptions = {};
      const keepKeys = Object.keys(prev)
        .map(Number)
        .filter(k => k !== idx)
        .sort((a, b) => a - b);
      let j = 0;
      for (const k of keepKeys) {
        newOptions[j++] = prev[k];
      }
      return newOptions;
    });
  };

  const getCitiesForEducation = async (provinceId, idx) => {
    setForm(cur => {
      const copy = { ...cur };
      const ed = copy.educationalBackground.slice();
      ed[idx] = {
        ...ed[idx],
        provinceId: Number(provinceId),
        institutionCityId: 0,
      };
      copy.educationalBackground = ed;
      return copy;
    });

    setEducationalCityOptions(prev => ({
      ...prev,
      [idx]: [{ value: 0, label: "..." }],
    }));

    setInfoLoading(true);
    try {
      const res = await getMethod("/address/v1/city/show/" + provinceId);
      const cities = res.data || [];
      const optionCity = [
        { value: 0, label: "..." },
        ...cities.map(c => ({ value: c.id, label: c.faName })),
      ];
      setEducationalCityOptions(prev => ({ ...prev, [idx]: optionCity }));
    } catch (e) {
      //
    } finally {
      setInfoLoading(false);
    }
  };

  const handleChangeEducationCity = (opt, idx) => {
    const val = opt?.value ?? 0;
    setForm(cur => {
      const copy = { ...cur };
      const ed = copy.educationalBackground.slice();
      ed[idx] = { ...ed[idx], institutionCityId: val };
      copy.educationalBackground = ed;
      return copy;
    });
  };

  const addCareer = () => {
    setForm(cur => ({
      ...cur,
      careerExperiences: [
        ...cur.careerExperiences,
        {
          companyName: "",
          position: "",
          duration: "",
          startDate: "",
          endDate: "",
          lastSalary: null,
          ReasonOfLeaving: "",
          phone: "",
        },
      ],
    }));
  };

  const removeCareer = idx => {
    setForm(cur => ({
      ...cur,
      careerExperiences: cur.careerExperiences.filter((_, i) => i !== idx),
    }));
  };

  const validateDate = d => {
    if (!d) return false;
    return /^\d{4}-\d{2}-\d{2}$/.test(d);
  };

  const findFormErrors = () => {
    const newErrors = {};

    if (!form.name || form.name.length < 2)
      newErrors.name = "نام الزامی است (حداقل 2 کاراکتر)";

    if (!form.lName || form.lName.length < 2)
      newErrors.lName = "نام خانوادگی الزامی است (حداقل 2 کاراکتر)";

    if (!form.fatherName || form.fatherName.length < 2)
      newErrors.fatherName = "نام پدر الزامی است (حداقل 2 کاراکتر)";

    if (!form.birthDate || !validateDate(form.birthDate))
      newErrors.birthDate = "تاریخ تولد الزامی است";

    if (!form.nationalCode || form.nationalCode.length !== 10)
      newErrors.nationalCode = "کد ملی باید 10 رقم باشد";

    if (!form.birthCertificateCityId || form.birthCertificateCityId.length < 2)
      newErrors.birthCertificateCityId = "شهر محل صدور شناسنامه الزامی است";

    if (!form.birthPlaceCityId || form.birthPlaceCityId.length < 2)
      newErrors.birthPlaceCityId = "شهر محل تولد الزامی است";

    if (!form.religion || form.religion.length < 2)
      newErrors.religion = "دین الزامی است";
    if (!form.nationality || form.nationality.length < 2)
      newErrors.nationality = "ملیت الزامی است";

    // health description conditional
    if (
      form.isHealthy === 0 &&
      (!form.healthDescription === 1 || form.healthDescription.length < 2)
    )
      newErrors.healthDescription = "توضیحات وضعیت سلامتی الزامی است";

    if (!form.militaryStatus || form.militaryStatus.length < 2)
      newErrors.militaryStatus = "وضعیت نظام وظیفه الزامی است";

    // educationalBackground items validation
    form.educationalBackground.forEach((ed, idx) => {
      if (!ed.certificate || ed.certificate.length < 2)
        newErrors[`educationalBackground.${idx}.certificate`] =
          "مدرک تحصیلی الزامی است (حداقل 2 کاراکتر)";
      if (!ed.field || ed.field.length < 2)
        newErrors[`educationalBackground.${idx}.field`] =
          "رشته تحصیلی الزامی است (حداقل 2 کاراکتر)";
      if (ed.startDate && !validateDate(ed.startDate))
        newErrors[`educationalBackground.${idx}.startDate`] =
          "تاریخ شروع باید در فرمت YYYY-MM-DD باشد";
      if (ed.endDate && !validateDate(ed.endDate))
        newErrors[`educationalBackground.${idx}.endDate`] =
          "تاریخ پایان باید در فرمت YYYY-MM-DD باشد";
      if (
        String(ed.academicAverage ?? "").trim() &&
        (Number.isNaN(Number(ed.academicAverage)) ||
          Number(ed.academicAverage) < 0 ||
          Number(ed.academicAverage) > 20)
      )
        newErrors[`educationalBackground.${idx}.academicAverage`] =
          "معدل باید یک عدد بین 0 تا 20 باشد";
      if (ed.institutionCityId && Number(ed.institutionCityId) < 1)
        newErrors[`educationalBackground.${idx}.institutionCityId`] =
          "شهر موسسه معتبر نیست";

      if (!ed.institutionName || ed.institutionName.length < 2)
        newErrors[`educationalBackground.${idx}.institutionName`] =
          "نام موسسه آموزشی الزامی است";

      if (!ed.startDate || !validateDate(ed.startDate))
        newErrors[`educationalBackground.${idx}.startDate`] =
          "تاریخ شروع الزامی است یا فرمت اصلاح شود (YYYY-MM-DD)";
      if (!ed.endDate || !validateDate(ed.endDate))
        newErrors[`educationalBackground.${idx}.endDate`] =
          "تاریخ پایان الزامی است یا فرمت اصلاح شود (YYYY-MM-DD)";
    });

    // careerExperiences items validation (all required per backend)
    form.careerExperiences.forEach((c, idx) => {
      if (!c.companyName || c.companyName.length < 2)
        newErrors[`careerExperiences.${idx}.companyName`] =
          "نام شرکت الزامی است";
      if (!c.position || c.position.length < 2)
        newErrors[`careerExperiences.${idx}.position`] = "سمت/شرح الزامی است";
      if (!c.duration || c.duration.length < 2)
        newErrors[`careerExperiences.${idx}.duration`] =
          "مدت همکاری الزامی است";
      if (!c.startDate || !validateDate(c.startDate))
        newErrors[`careerExperiences.${idx}.startDate`] =
          "تاریخ شروع الزامی است یا فرمت اصلاح شود (YYYY-MM-DD)";
      if (!c.endDate || !validateDate(c.endDate))
        newErrors[`careerExperiences.${idx}.endDate`] =
          "تاریخ پایان الزامی است یا فرمت اصلاح شود (YYYY-MM-DD)";
      if (!c.lastSalary || Number(c.lastSalary) < 1)
        newErrors[`careerExperiences.${idx}.lastSalary`] =
          "آخرین حقوق دریافتی الزامی است و باید عدد بزرگتر از 0 باشد";
      if (!c.ReasonOfLeaving || c.ReasonOfLeaving.length < 2)
        newErrors[`careerExperiences.${idx}.ReasonOfLeaving`] =
          "علت ترک کار الزامی است";
    });

    // englishLevel must be 1..4
    if (![1, 2, 3, 4].includes(Number(form.englishLevel)))
      newErrors.englishLevel = "سطح زبان انگلیسی را انتخاب کنید";

    [
      "computerSkillsWindows",
      "computerSkillsWord",
      "computerSkillsExcel",
      "computerSkillsImageEditing",
      "computerSkillsVideoEditing",
    ].forEach(k => {
      if (form[k] !== 0 && form[k] !== 1) newErrors[k] = `${k} الزامی است`;
    });

    // cooperateType must be 1..3
    if (![1, 2, 3].includes(Number(form.cooperateType)))
      newErrors.cooperateType = "نوع همکاری درخواستی را انتخاب کنید";

    if (!form.requestedJob || form.requestedJob.length < 2)
      newErrors.requestedJob = "شغل درخواستی الزامی است";

    // insurance conditional
    if (form.haveInsuranceHistory !== 0 && form.haveInsuranceHistory !== 1)
      newErrors.haveInsuranceHistory = "مشخص کردن سابقه بیمه الزامی است";
    if (form.haveInsuranceHistory === 1) {
      if (!form.insuranceHistoryDuration)
        newErrors.insuranceHistoryDuration = "مدت سابقه بیمه الزامی است";
    }

    if (!form.howToHeardAboutUs)
      newErrors.howToHeardAboutUs = "نحوه آشنایی الزامی است";

    // required booleans for willingness etc.
    [
      "willingToTravelForJob",
      "willingToTrialPeriod",
      "workingRightNow",
      "canGuarantee",
    ].forEach(k => {
      if (form[k] !== 0 && form[k] !== 1) newErrors[k] = `${k} الزامی است`;
    });

    if (
      !Number.isInteger(Number(form.provinceId)) ||
      Number(form.provinceId) < 1
    )
      newErrors.provinceId = "استان الزامی است";
    if (!Number.isInteger(Number(form.cityId)) || Number(form.cityId) < 1)
      newErrors.cityId = "شهر الزامی است";

    if (!form.address || form.address.length < 1)
      newErrors.address = "آدرس الزامی است";

    if (!form.mobile || form.mobile === "") {
      newErrors.mobile = "موبایل الزامی است";
    } else if (form.mobile.length !== 11) {
      newErrors.mobile = "شماره موبایل باید 11 رقم باشد (09xxxxxxxxx)";
    } else if (Number(form.mobile[0]) !== 0 || Number(form.mobile[1]) !== 9) {
      newErrors.mobile = "شماره موبایل معتبر نمی باشد";
    }

    if (form.email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email))
      newErrors.email = "ایمیل نامعتبر است";

    if (form.confirmInformation !== 1)
      newErrors.confirmInformation = "تایید اطلاعات الزامی است";

    return newErrors;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const newErrors = findFormErrors();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);

      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      await sendData();
    }
  };

  const sendData = async () => {
    setLoading(true);

    try {
      // shallow copy و حذف فیلدهایی که backend نیاز نداره
      const payload = { ...form };
      delete payload.birthCertificateProvinceId;
      delete payload.birthPlaceProvinceId;

      if (payload.educationalBackground.length === 0)
        delete payload.educationalBackground;

      if (payload?.careerExperiences.length === 0)
        delete payload.careerExperiences;

      let fd;
      if (payload?.files.length > 0) {
        fd = buildFormData(payload, ["files"]);
      } else {
        delete payload.files;

        fd = buildFormData(payload);
      }
      const response = await postMethodWithFile("/v1/personnel/create", fd);

      setLoading(false);

      if (response?.status === 200) {
        setForm(initialFormState);
        setErrors({});

        Swal.fire({
          icon: "success",
          text:
            response.data?.message ||
            response?.message ||
            "ثبت با موفقیت انجام شد",
        }).then(() => {
          navigate("/", { replace: true });
        });
      } else {
        Swal.fire({
          icon: "error",
          text:
            response?.data?.data ||
            response?.data?.message ||
            response?.message ||
            "خطا در ارسال",
        });
      }
    } catch (e) {
      setLoading(false);
      Swal.fire({
        icon: "error",
        text:
          e?.response?.data?.data ||
          e?.response?.data?.message ||
          "خطا در ارسال",
      });
    }
  };

  return (
    <Fragment>
      <Menu />

      <Container className="loginContainer form-personnel-register">
        <Row className="justify-content-center">
          <Col xs={12} sm={12} lg={10} xl={10}>
            <Card className="loginForm">
              <Card.Body>
                <Row className="pb-4">
                  <Col xs={12}>
                    <h3>فرم درخواست همکاری</h3>
                    <h6>
                      <span>این فرم مربوط به اخذ نمایندگی نیست، </span>
                      <Link to="/register/agent">
                        لطفا فرم دیگری از طریق این لینک را تکمیل نمایید.
                      </Link>
                    </h6>
                  </Col>
                </Row>
                <Form onSubmit={handleSubmit} encType="multipart/form-data">
                  <Row>
                    {/* name */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          نام:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.name}
                          type="text"
                          onChange={e => setField("name", e.target.value)}
                          isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* lName */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          نام خانوادگی:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.lName}
                          type="text"
                          onChange={e => setField("lName", e.target.value)}
                          isInvalid={!!errors.lName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.lName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* fatherName */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          نام پدر:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.fatherName}
                          type="text"
                          onChange={e => setField("fatherName", e.target.value)}
                          isInvalid={!!errors.fatherName}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.fatherName}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          کد ملی:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.nationalCode}
                          type="text"
                          onChange={e => {
                            const value = convertToEnglishNumbers(
                              e.target.value
                            );
                            setField("nationalCode", value);
                          }}
                          isInvalid={!!errors.nationalCode}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.nationalCode}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>شماره شناسنامه</Form.Label>
                        <Form.Control
                          value={form.birthCertificateNumber}
                          type="text"
                          onChange={e => {
                            const value = convertToEnglishNumbers(
                              e.target.value
                            );
                            setField("birthCertificateNumber", value);
                          }}
                          isInvalid={!!errors.birthCertificateNumber}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.birthCertificateNumber}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          تاریخ تولد:<span className="text-danger">*</span>
                        </Form.Label>
                        <DateFilter
                          onChange={date => {
                            setField("birthDate", date);
                          }}
                          label=""
                        />
                        {errors.birthDate && (
                          <div className="invalid-feedback d-block">
                            {errors.birthDate}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          استان:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          onChange={e => getCities(e.target.value)}
                          value={form.provinceId}
                          isInvalid={!!errors.provinceId}
                        >
                          <option value={0}>...</option>
                          {provinces.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.faName}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.provinceId}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          شهر:<span className="text-danger">*</span>
                        </Form.Label>
                        <div className="dropdown-container">
                          <Select
                            options={cityOption}
                            value={cityOption.find(
                              c => c.value === form.cityId
                            )}
                            onChange={handleChangeCity}
                            isInvalid={!!errors.cityId}
                          />
                        </div>

                        {errors.cityId && (
                          <div className="invalid-feedback d-block">
                            {errors.cityId}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* address */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          آدرس:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          as="textarea"
                          value={form.address}
                          onChange={e => setField("address", e.target.value)}
                          isInvalid={!!errors.address}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.address}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* mobile */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          موبایل:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.mobile}
                          onChange={e => {
                            const value = convertToEnglishNumbers(
                              e.target.value
                            );
                            setField("mobile", value);
                          }}
                          isInvalid={!!errors.mobile}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.mobile}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>تلفن ثابت:</Form.Label>
                        <Form.Control
                          value={form.phone}
                          onChange={e => {
                            const value = convertToEnglishNumbers(
                              e.target.value
                            );
                            setField("phone", value);
                          }}
                        />
                      </Form.Group>
                    </Col>

                    {/* email */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>ایمیل:</Form.Label>
                        <Form.Control
                          value={form.email}
                          onChange={e => setField("email", e.target.value)}
                          isInvalid={!!errors.email}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.email}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* religion */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          دین:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.religion}
                          type="text"
                          onChange={e => setField("religion", e.target.value)}
                          isInvalid={!!errors.religion}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.religion}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* nationality */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          ملیت:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.nationality}
                          type="text"
                          onChange={e =>
                            setField("nationality", e.target.value)
                          }
                          isInvalid={!!errors.nationality}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.nationality}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* requestedJob */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          شغل درخواستی:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.requestedJob}
                          type="text"
                          onChange={e =>
                            setField("requestedJob", e.target.value)
                          }
                          isInvalid={!!errors.requestedJob}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.requestedJob}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    {/* englishLevel */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          سطح زبان انگلیسی:
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          placeholder="انتخاب کنید"
                          options={englishOptions}
                          value={englishOptions.find(
                            e => e.value === Number(form.englishLevel)
                          )}
                          onChange={opt => setField("englishLevel", opt?.value)}
                          isInvalid={!!errors.englishLevel}
                        />

                        {errors.englishLevel && (
                          <div className="invalid-feedback d-block">
                            {errors.englishLevel}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* birth certificate province & city */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          استان محل صدور شناسنامه:
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          onChange={e =>
                            getCitiesForBirthCertificate(e.target.value)
                          }
                          value={form.birthCertificateProvinceId || 0}
                        >
                          <option value={0}>...</option>
                          {provinces.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.faName}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          شهر محل صدور شناسنامه:
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <div className="dropdown-container">
                          <Select
                            options={birthCertCityOptions}
                            value={birthCertCityOptions.find(
                              c => c.value === form.birthCertificateCityId
                            )}
                            onChange={handleChangeBirthCertificateCity}
                            isInvalid={!!errors.birthCertificateCityId}
                          />
                        </div>

                        {errors.birthCertificateCityId && (
                          <div className="invalid-feedback d-block">
                            {errors.birthCertificateCityId}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* birth place province & city */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          استان محل تولد:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Select
                          onChange={e => getCitiesForBirthPlace(e.target.value)}
                          value={form.birthPlaceProvinceId || 0}
                        >
                          <option value={0}>...</option>
                          {provinces.map(p => (
                            <option key={p.id} value={p.id}>
                              {p.faName}
                            </option>
                          ))}
                        </Form.Select>
                        {errors.birthPlaceProvinceId && (
                          <div className="invalid-feedback d-block">
                            {errors.birthPlaceProvinceId}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          شهر محل تولد:<span className="text-danger">*</span>
                        </Form.Label>
                        <div className="dropdown-container">
                          <Select
                            options={birthPlaceCityOptions}
                            value={birthPlaceCityOptions.find(
                              c => c.value === form.birthPlaceCityId
                            )}
                            onChange={handleChangeBirthPlaceCity}
                            isInvalid={!!errors.birthPlaceCityId}
                          />
                        </div>

                        {errors.birthPlaceCityId && (
                          <div className="invalid-feedback d-block">
                            {errors.birthPlaceCityId}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    {/* cooperateType */}
                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          نوع همکاری درخواستی:
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Select
                          placeholder="انتخاب کنید"
                          options={cooperateOptions}
                          value={cooperateOptions.find(
                            c => c.value === Number(form.cooperateType)
                          )}
                          onChange={opt =>
                            setField("cooperateType", opt?.value)
                          }
                          isInvalid={!!errors.cooperateType}
                        />

                        {errors.cooperateType && (
                          <div className="invalid-feedback d-block">
                            {errors.cooperateType}
                          </div>
                        )}
                      </Form.Group>
                    </Col>

                    <div className="w-100">
                      <Col xs={12} md={6} lg={4}>
                        <Form.Group>
                          <Form.Label>
                            وضعیت نظام وظیفه:
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Select
                            placeholder="انتخاب کنید"
                            options={militaryOptions}
                            value={militaryOptions.find(
                              m => m.value === Number(form.militaryStatus)
                            )}
                            onChange={opt =>
                              setField("militaryStatus", opt?.value)
                            }
                          />
                          {errors.militaryStatus && (
                            <div className="invalid-feedback d-block">
                              {errors.militaryStatus}
                            </div>
                          )}
                        </Form.Group>
                      </Col>
                    </div>

                    <Col xs={12} className="mt-3 border border-2 rounded p-2">
                      <Form.Group>
                        <Form.Label>
                          وضعیت تأهل:<span className="text-danger">*</span>
                        </Form.Label>
                        <div>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="متأهل"
                            type="radio"
                            name="isMarried"
                            checked={form.isMarried === 1}
                            onChange={() => setField("isMarried", 1)}
                          />
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="مجرد"
                            type="radio"
                            name="isMarried"
                            checked={form.isMarried === 0}
                            onChange={() => setField("isMarried", 0)}
                          />
                        </div>
                      </Form.Group>
                    </Col>

                    <Col xs={12} className="mt-3 border border-2 rounded p-2">
                      <Form.Group>
                        <Form.Label>
                          وضعیت سلامت:<span className="text-danger">*</span>
                        </Form.Label>
                        <div>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="در سلامت کامل هستم"
                            type="radio"
                            name="health"
                            checked={form.isHealthy === 1}
                            onChange={() => setField("isHealthy", 1)}
                          />
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="خیر"
                            type="radio"
                            name="health"
                            checked={form.isHealthy === 0}
                            onChange={() => setField("isHealthy", 0)}
                          />
                        </div>
                        {form.isHealthy === 0 && (
                          <Form.Control
                            placeholder="توضیح دهید"
                            className="mt-2"
                            value={form.healthDescription}
                            onChange={e =>
                              setField("healthDescription", e.target.value)
                            }
                            isInvalid={!!errors.healthDescription}
                          />
                        )}
                        <Form.Control.Feedback type="invalid">
                          {errors.healthDescription}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} className="mt-3 border border-2 rounded p-2">
                      <Form.Label>
                        مهارت‌های کامپیوتری:
                        <span className="text-danger">*</span>
                      </Form.Label>

                      <div className="d-flex flex-wrap row-gap-2">
                        <Col xs={12}>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="مسلط به windows (پرینتر، تنظیمات، اینترنت و ...)"
                            type="checkbox"
                            checked={form.computerSkillsWindows}
                            onChange={e =>
                              setField(
                                "computerSkillsWindows",
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </Col>

                        <Col xs={12}>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="مسلط به word"
                            type="checkbox"
                            checked={form.computerSkillsWord}
                            onChange={e =>
                              setField(
                                "computerSkillsWord",
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </Col>

                        <Col xs={12}>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="مسلط به excel"
                            type="checkbox"
                            checked={form.computerSkillsExcel}
                            onChange={e =>
                              setField(
                                "computerSkillsExcel",
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </Col>

                        <Col xs={12}>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="تجربه کار با نرم افزار های ویرایش تصویر (photoshop، lightroom و ...)"
                            type="checkbox"
                            checked={form.computerSkillsImageEditing}
                            onChange={e =>
                              setField(
                                "computerSkillsImageEditing",
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </Col>

                        <Col xs={12}>
                          <Form.Check
                            className="form-check-reverse"
                            inline
                            label="تجربه کار با نرم افزار های ویرایش ویدیو (premiere، after effects و ...)"
                            type="checkbox"
                            checked={form.computerSkillsVideoEditing}
                            onChange={e =>
                              setField(
                                "computerSkillsVideoEditing",
                                e.target.checked ? 1 : 0
                              )
                            }
                          />
                        </Col>
                      </div>

                      <div className="invalid-feedback d-block">
                        {errors.computerSkillsWindows ||
                          errors.computerSkillsWord ||
                          errors.computerSkillsExcel ||
                          errors.computerSkillsImageEditing ||
                          errors.computerSkillsVideoEditing}
                      </div>
                    </Col>

                    <Col xs={12} className="mt-3">
                      <h5>سوابق تحصیلی</h5>
                      {form.educationalBackground.map((ed, idx) => (
                        <Card key={idx} className="mb-2 p-2">
                          <Row>
                            {/* certificate, field, academicAverage, institutionName, province/institutionCity, DateRangePicker */}
                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>
                                مدرک:<span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="مدرک"
                                  value={ed.certificate}
                                  onChange={e => {
                                    const copy = [
                                      ...form.educationalBackground,
                                    ];
                                    copy[idx].certificate = e.target.value;
                                    setField("educationalBackground", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `educationalBackground.${idx}.certificate`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `educationalBackground.${idx}.certificate`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>
                                رشته:<span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="رشته"
                                  value={ed.field}
                                  onChange={e => {
                                    const copy = [
                                      ...form.educationalBackground,
                                    ];
                                    copy[idx].field = e.target.value;
                                    setField("educationalBackground", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `educationalBackground.${idx}.field`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors[`educationalBackground.${idx}.field`]}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>معدل:</Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="معدل"
                                  value={ed.academicAverage}
                                  onChange={e => {
                                    const copy = [
                                      ...form.educationalBackground,
                                    ];
                                    copy[idx].academicAverage =
                                      convertToEnglishNumbers(e.target.value);
                                    setField("educationalBackground", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `educationalBackground.${idx}.academicAverage`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `educationalBackground.${idx}.academicAverage`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>
                                نام موسسه آموزشی:
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="نام موسسه آموزشی"
                                  value={ed.institutionName}
                                  onChange={e => {
                                    const copy = [
                                      ...form.educationalBackground,
                                    ];
                                    copy[idx].institutionName = e.target.value;
                                    setField("educationalBackground", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `educationalBackground.${idx}.institutionName`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `educationalBackground.${idx}.institutionName`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Group>
                                <Form.Label>استان موسسه آموزشی:</Form.Label>
                                <Form.Select
                                  onChange={e =>
                                    getCitiesForEducation(e.target.value, idx)
                                  }
                                  value={ed.provinceId || 0}
                                  isInvalid={
                                    !!errors[
                                      `educationalBackground.${idx}.provinceId`
                                    ]
                                  }
                                >
                                  <option value={0}>...</option>
                                  {provinces.map(p => (
                                    <option key={p.id} value={p.id}>
                                      {p.faName}
                                    </option>
                                  ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `educationalBackground.${idx}.provinceId`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Group>
                                <Form.Label>شهر موسسه آموزشی:</Form.Label>
                                <div className="dropdown-container">
                                  <Select
                                    options={
                                      educationalCityOptions[idx] || [
                                        { value: 0, label: "..." },
                                      ]
                                    }
                                    value={
                                      (educationalCityOptions[idx] || []).find(
                                        c => c.value === ed.institutionCityId
                                      ) ||
                                      (educationalCityOptions[idx] || [])[0]
                                    }
                                    onChange={opt =>
                                      handleChangeEducationCity(opt, idx)
                                    }
                                    isInvalid={
                                      !!errors[
                                        `educationalBackground.${idx}.institutionCityId`
                                      ]
                                    }
                                  />
                                </div>
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `educationalBackground.${idx}.institutionCityId`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <DateRangePicker
                              startValue={ed.startDate}
                              endValue={ed.endDate}
                              setStart={val => {
                                const copy = [...form.educationalBackground];
                                copy[idx].startDate = val;
                                setField("educationalBackground", copy);
                              }}
                              setEnd={val => {
                                const copy = [...form.educationalBackground];
                                copy[idx].endDate = val;
                                setField("educationalBackground", copy);
                              }}
                              startError={
                                errors[`educationalBackground.${idx}.startDate`]
                              }
                              endError={
                                errors[`educationalBackground.${idx}.endDate`]
                              }
                              startLabel="تاریخ شروع"
                              endLabel="تاریخ پایان"
                              colProps={{ xs: 12, md: 6 }}
                            />

                            <Col
                              xs={12}
                              md={6}
                              className="mt-2 d-flex align-items-center"
                            >
                              <Button
                                variant="danger"
                                onClick={() => removeEducation(idx)}
                              >
                                حذف
                              </Button>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button className="mt-2" onClick={addEducation}>
                        افزودن سابقه تحصیلی
                      </Button>
                    </Col>

                    {/* careerExperiences */}
                    <Col xs={12} className="mt-3">
                      <h5>سوابق شغلی</h5>
                      {form.careerExperiences.map((c, idx) => (
                        <Card key={idx} className="mb-2 p-2">
                          <Row>
                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>
                                نام شرکت:<span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="نام شرکت"
                                  value={c.companyName}
                                  onChange={e => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].companyName = e.target.value;
                                    setField("careerExperiences", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `careerExperiences.${idx}.companyName`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `careerExperiences.${idx}.companyName`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4}>
                              <Form.Label>
                                سمت/شرح:<span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="سمت/شرح"
                                  value={c.position}
                                  onChange={e => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].position = e.target.value;
                                    setField("careerExperiences", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `careerExperiences.${idx}.position`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors[`careerExperiences.${idx}.position`]}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4} className="mt-2">
                              <Form.Label>
                                مدت همکاری:
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="مثلا 10 ماه / 10 سال"
                                  value={c.duration}
                                  onChange={e => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].duration = e.target.value;
                                    setField("careerExperiences", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `careerExperiences.${idx}.duration`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {errors[`careerExperiences.${idx}.duration`]}
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <DateRangePicker
                              startValue={c.startDate}
                              endValue={c.endDate}
                              setStart={val => {
                                const copy = [...form.careerExperiences];
                                copy[idx].startDate = val;
                                setField("careerExperiences", copy);
                              }}
                              setEnd={val => {
                                const copy = [...form.careerExperiences];
                                copy[idx].endDate = val;
                                setField("careerExperiences", copy);
                              }}
                              startError={
                                errors[`careerExperiences.${idx}.startDate`]
                              }
                              endError={
                                errors[`careerExperiences.${idx}.endDate`]
                              }
                              startLabel="تاریخ شروع"
                              endLabel="تاریخ پایان"
                              colProps={{ xs: 12, md: 6 }}
                            />

                            <Col xs={12} md={6} lg={4} className="mt-2">
                              <Form.Label>
                                آخرین حقوق دریافتی:
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <NumericInput
                                  placeholder="آخرین حقوق دریافتی"
                                  className="form-control"
                                  value={c.lastSalary || ""}
                                  onValueChange={({ value }) => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].lastSalary = value;
                                    setField("careerExperiences", copy);
                                  }}
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `careerExperiences.${idx}.lastSalary`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4} className="mt-2">
                              <Form.Label>
                                علت ترک کار:
                                <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="علت ترک کار"
                                  value={c.ReasonOfLeaving}
                                  onChange={e => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].ReasonOfLeaving = e.target.value;
                                    setField("careerExperiences", copy);
                                  }}
                                  isInvalid={
                                    !!errors[
                                      `careerExperiences.${idx}.ReasonOfLeaving`
                                    ]
                                  }
                                />
                                <Form.Control.Feedback type="invalid">
                                  {
                                    errors[
                                      `careerExperiences.${idx}.ReasonOfLeaving`
                                    ]
                                  }
                                </Form.Control.Feedback>
                              </Form.Group>
                            </Col>

                            <Col xs={12} md={6} lg={4} className="mt-2">
                              <Form.Label>تلفن محل کار:</Form.Label>
                              <Form.Group>
                                <Form.Control
                                  placeholder="تلفن محل کار"
                                  value={c.phone}
                                  onChange={e => {
                                    const copy = [...form.careerExperiences];
                                    copy[idx].phone = convertToEnglishNumbers(
                                      e.target.value
                                    );
                                    setField("careerExperiences", copy);
                                  }}
                                />
                              </Form.Group>
                            </Col>

                            <Col
                              xs={12}
                              md={6}
                              className="mt-2 d-flex align-items-center"
                            >
                              <Button
                                variant="danger"
                                onClick={() => removeCareer(idx)}
                              >
                                حذف
                              </Button>
                            </Col>
                          </Row>
                        </Card>
                      ))}
                      <Button className="mt-2" onClick={addCareer}>
                        افزودن سابقه شغلی
                      </Button>
                    </Col>

                    <Col xs={12} className="my-3 border border-2 rounded p-2">
                      <Form.Check
                        className="form-check-reverse"
                        type="checkbox"
                        label="سابقه بیمه دارم"
                        checked={form.haveInsuranceHistory}
                        onChange={e =>
                          setField(
                            "haveInsuranceHistory",
                            e.target.checked ? 1 : 0
                          )
                        }
                      />

                      {form.haveInsuranceHistory === 1 && (
                        <Col xs={12} className="mt-2">
                          <Form.Group>
                            <Form.Label>
                              مدت سابقه بیمه:
                              <span className="text-danger">*</span>
                            </Form.Label>
                            <Form.Control
                              value={form.insuranceHistoryDuration}
                              onChange={e =>
                                setField(
                                  "insuranceHistoryDuration",
                                  e.target.value
                                )
                              }
                              isInvalid={!!errors.insuranceHistoryDuration}
                            />
                            <Form.Control.Feedback type="invalid">
                              {errors.insuranceHistoryDuration}
                            </Form.Control.Feedback>
                          </Form.Group>
                        </Col>
                      )}
                    </Col>

                    <Col xs={12} md={6} lg={4}>
                      <Form.Group>
                        <Form.Label>
                          نحوه آشنایی:<span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          value={form.howToHeardAboutUs}
                          onChange={e =>
                            setField("howToHeardAboutUs", e.target.value)
                          }
                          isInvalid={!!errors.howToHeardAboutUs}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.howToHeardAboutUs}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>

                    <Col xs={12} className="my-3 border border-2 rounded p-2">
                      <Form.Label>شرایط کاری:</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        <Form.Check
                          className="form-check-reverse"
                          inline
                          label="حاضر به سفر کاری"
                          type="checkbox"
                          checked={form.willingToTravelForJob}
                          onChange={e =>
                            setField(
                              "willingToTravelForJob",
                              e.target.checked ? 1 : 0
                            )
                          }
                        />
                        <Form.Check
                          className="form-check-reverse"
                          inline
                          label="حاضر به دوره آزمایشی"
                          type="checkbox"
                          checked={form.willingToTrialPeriod}
                          onChange={e =>
                            setField(
                              "willingToTrialPeriod",
                              e.target.checked ? 1 : 0
                            )
                          }
                        />
                        <Form.Check
                          className="form-check-reverse"
                          inline
                          label="در حال حاضر شاغلم"
                          type="checkbox"
                          checked={form.workingRightNow}
                          onChange={e =>
                            setField(
                              "workingRightNow",
                              e.target.checked ? 1 : 0
                            )
                          }
                        />
                        <Form.Check
                          className="form-check-reverse"
                          inline
                          label="قابلیت ضمانت دارم"
                          type="checkbox"
                          checked={form.canGuarantee}
                          onChange={e =>
                            setField("canGuarantee", e.target.checked ? 1 : 0)
                          }
                        />
                      </div>
                      <div className="invalid-feedback d-block">
                        {errors.willingToTravelForJob ||
                          errors.willingToTrialPeriod ||
                          errors.workingRightNow ||
                          errors.canGuarantee}
                      </div>
                    </Col>

                    <ResumeUploader
                      files={form.files}
                      setFiles={files => setForm(cur => ({ ...cur, files }))}
                      errors={errors}
                      setErrors={setErrors}
                    />

                    <Col xs={12} md={6} lg={4} className="mt-3">
                      <Form.Check
                        className="form-check-reverse"
                        type="checkbox"
                        id="confirmInformation"
                        label="اطلاعات فوق را به درستی تأیید می‌کنم"
                        checked={form.confirmInformation === 1}
                        onChange={e =>
                          setField(
                            "confirmInformation",
                            e.target.checked ? 1 : 0
                          )
                        }
                        isInvalid={!!errors.confirmInformation}
                      />
                      <div className="invalid-feedback d-block">
                        {errors.confirmInformation}
                      </div>
                    </Col>

                    {/* submit */}
                    <Col xs={12} className="mt-4 d-flex justify-content-end">
                      {loading ? (
                        <Button className="loginButton">
                          <div className="d-flex align-items-center">
                            <Spinner
                              style={{
                                width: "20px",
                                height: "20px",
                                fontSize: "0.8rem",
                              }}
                            />
                            <span className="me-1"> در حال ذخیره</span>
                          </div>
                        </Button>
                      ) : (
                        <Button
                          type="submit"
                          className="loginButton"
                          disabled={infoLoading}
                        >
                          ارسال
                        </Button>
                      )}
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <Footer />
    </Fragment>
  );
}

export default PersonnelRegister;
